import Analytics from '@opencare/analytics';
import _ from 'lodash';
import moment from 'moment';

import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  FlexRow,
  H1,
  MarketingButton,
  P,
  StyledFormItem,
} from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import {
  getInsuranceList,
  getInsuranceListForCanada,
  updateCheckoutErrorStatus,
  updateCheckoutFormData,
  updateInsuranceProviderAndPlan,
  updatePatientDetails,
} from 'connect/actions';
import {
  selectDataAddressStateShort,
  selectDataCountry,
  selectDataInsurance,
  selectDataLoadingUser,
  selectDataPatient,
  selectDataRequestedAppointment,
  selectDataUser,
} from 'connect/selectors';
import { Form } from 'opencare-crown';

import { PartnerKey } from 'pages/partner/constants';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useRedirectionService } from 'services/RedirectionService';

import { COUNTRY } from '../../../../../common/constants';
import { PARTNER_PATH } from '../../../../../containers/App/Routes';
import Error from '../../../../common/components/Error/Error';
import { usePartnerStorage } from '../../../../partner/storage/Storage';
import { CHECKOUT_KEY } from '../../../../partner/utils';
import {
  NameBirthContainer,
  StepLoading,
  StyledAlert,
  StyledLock,
} from '../../../checkout-styled-components';
import { selectDataCheckoutFormErrorStatus } from '../../../connect/selectors';
import {
  checkMemberIdFormat,
  formatDateOfBirth,
  makeInsuranceState,
} from '../../../helpers/insurance-helpers';
import { BirthDateFormRow } from '../../common/BirthdateFormRow/BirthdateFormRow';
import {
  CheckoutInsuranceForm,
  ContextType,
} from '../../common/CheckoutInsuranceForm/CheckoutInsuranceForm';
import MemberIDHelperModal from '../../common/MemberIDHelperModal/MemberIDHelperModal';
import { NameFormRow } from '../../common/NameFormRow/NameFormRow';

import { Fieldset, MemberIdModal, OpacityDiv } from './styled-components';

const analytics = new Analytics();

// Component
const InsuranceStep = ({ handleNextStep, state }) => {
  const dispatch = useDispatch();
  const storage = usePartnerStorage();
  const [insuranceForm] = Form.useForm();

  const analyticsService = useAnalyticsService();
  const redirectionService = useRedirectionService();

  const country = useSelector(selectDataCountry);
  const region = useSelector(selectDataAddressStateShort);
  const insurance = useSelector(selectDataInsurance);
  const patient = useSelector(selectDataPatient);
  const loadingUser = useSelector(selectDataLoadingUser);
  const user = useSelector(selectDataUser);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );

  const [insuranceMemberInformation, setInsuranceMemberInformation] = useState({
    isPrimaryMember:
      country === COUNTRY.unitedStates
      && typeof insurance.isPrimaryHolder !== 'undefined'
        ? insurance.isPrimaryHolder
        : true,
    memberfirstName: state?.memberfirstName || '',
    memberlastName: state?.memberlastName || '',
    memberbirthYear: state?.dateOfBirth?.[0] || null,
    memberbirthMonth: state?.dateOfBirth?.[1] || null,
    memberbirthDay: state?.dateOfBirth?.[2] || null,
    memberId: insurance.memberId,
    groupNumber: insurance.groupNumber,
    employer: insurance.employer,
  });
  const updateInsuranceMemberInformation = (fieldName, value) => {
    const mapping = {
      firstName: 'memberfirstName',
      lastName: 'memberlastName',
      day: 'memberbirthDay',
      month: 'memberbirthMonth',
      year: 'memberbirthYear',
    };

    const mappedFieldName = mapping[fieldName] || fieldName;
    const newInsuranceMemberInformation = {
      ...insuranceMemberInformation,
      [mappedFieldName]: value,
    };

    // Reset fields if isPrimaryMember is unchecked
    if (mappedFieldName === 'isPrimaryMember' && !value) {
      newInsuranceMemberInformation.memberfirstName = '';
      newInsuranceMemberInformation.memberlastName = '';
      newInsuranceMemberInformation.memberbirthMonth = '';
      newInsuranceMemberInformation.memberbirthDay = '';
      newInsuranceMemberInformation.memberbirthYear = '';
    }

    setInsuranceMemberInformation(newInsuranceMemberInformation);
    insuranceForm.setFieldsValue(newInsuranceMemberInformation);
  };
  const { isPrimaryMember } = insuranceMemberInformation;
  const [insuranceProvider, setInsuranceProvider] = useState({
    name: insurance.provider,
    id: insurance.providerId,
  });
  const [insurancePlan, setInsurancePlan] = useState({
    name: insurance.plan,
    id: insurance.planId,
  });
  const [firstName, setFirstName] = useState(state?.memberfirstName || '');
  const [lastName, setLastName] = useState(state?.memberlastName || '');

  const memberIdIsOptional = user.partner.key === PartnerKey.HEALTHJOY;

  const onNameChange = (field, value) => {
    const updateFunc = {
      firstName: setFirstName,
      lastName: setLastName,
    };

    updateFunc[field]?.(value);
  };
  const [birthdate, setBirthdate] = useState({
    day: state?.dateOfBirth?.[2] || null,
    month: state?.dateOfBirth?.[1] || null,
    year: state?.dateOfBirth?.[0] || null,
  });
  const onBirthdateChange = (field, value) => {
    const fieldName = {
      day: 'birthDay',
      month: 'birthMonth',
      year: 'birthYear',
    }[field];

    insuranceForm.setFieldsValue({
      [fieldName]: value,
    });

    setBirthdate({
      ...birthdate,
      [field]: value,
    });
  };

  const [isMemberIDHelperVisible, setIsMemberIDHelperVisible] = useState(false);
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] = useState(
    true,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [stepComplete, setStepComplete] = useState(false);

  // On initialization check what required fields are left
  // This will enable the continue button if they're already filled
  useEffect(() => {
    if (user && state.isCleared) {
      // Cached data got cleared, redirect back to Partner Page
      redirectionService.redirect({
        url: PARTNER_PATH,
        reason: 'insurance-step-state-cleared',
      });
    }
  }, [user, state]);

  useEffect(() => {
    checkRequiredFields();
  }, [insuranceForm.getFieldsValue()]);

  // All necessary data is loaded
  useEffect(() => {
    if (isLoading) {
      updateInsuranceMemberInformation(
        'isPrimaryMember',
        isLoading && (state?.isPrimaryMember ?? true),
      );
    }
    setIsLoading(state.isLoading);
  }, [state]);

  // Initial page view event
  useEffect(() => {
    if (!insurance || !user) {
      return;
    }

    analytics.trackPageView({
      pageType: analytics.pageTypes.FORM,
      pageName: `partner_form-booking-patient-information${
        insurance.provider ? '-insurance' : ''
      }`,
      name: 'form-booking',
      section: insurance.provider
        ? 'patient-information-insurance'
        : 'patient-details',
      practiceId: _.get(requestedAppointment, 'provider.clinic'),
      providerId: _.get(requestedAppointment, 'providerId'),
      userId: user.id,
      partnerId: user.partner.id,
      patientId: state.id,
      ...analyticsService.analyticsData,
    });
  }, [insurance, user]);

  useEffect(() => {
    if (!loadingUser && formSubmitted) {
      if (checkoutFormErrorStatus === null) {
        storage.save(CHECKOUT_KEY.INSURANCE, formData);
        setStepComplete(true);
      } else {
        setFormSubmitted(false);
      }
    }
  }, [loadingUser]);

  // To move to the next step one of the following must be true:
  //   1. must be a new user who filled out the form
  //   2. or a user who is already signed in who clicks continue
  useEffect(() => {
    const canMoveToNextStep = stepComplete && !checkoutFormErrorStatus;

    if (canMoveToNextStep) {
      handleNextStep(formData);
    }
  }, [patient, stepComplete]);

  useEffect(() => {
    if (country === COUNTRY.canada) {
      dispatch(getInsuranceListForCanada());
    }
  }, [country]);

  useEffect(() => {
    if (region) {
      dispatch(getInsuranceList(region));
    }
  }, [region]);

  const checkMemberId = (_rule, value, callback) => {
    if (!value) {
      return callback();
    }

    const checkResult = checkMemberIdFormat(value);

    if (!checkResult.valid) {
      return callback(checkResult.description);
    }

    return callback();
  };

  const fireSubmitAnalytics = () => {
    let content = {
      first: insuranceForm.getFieldValue('firstName'),
      last: insuranceForm.getFieldValue('lastName'),
      birth: moment(
        `${insuranceForm.getFieldValue('birthYear')}-${moment()
          .month(
            typeof insuranceForm.getFieldValue('birthMonth') === 'number'
              ? insuranceForm.getFieldValue('birthMonth') - 1
              : insuranceForm.getFieldValue('birthMonth'),
          )
          .format('MM')}-${insuranceForm.getFieldValue('birthDay')}`,
      ).format('YYYY-MM-DD'),
      holder: isPrimaryMember ? 'yes' : 'no',
    };
    if (insurance.provider) {
      const insuranceContent = {
        'insurance-carrier': insuranceProvider.name,
        'member-id': _.toString(insuranceForm.getFieldValue('memberId')),
        employer: insuranceForm.getFieldValue('employer') || null,
      };
      if (country === COUNTRY.unitedStates) {
        insuranceContent['insurance-plan'] = insurancePlan.name;
        insuranceContent['group-id'] =
          insuranceForm.getFieldValue('groupId') || null;
      }
      content = _.merge(content, insuranceContent);
    }
    if (!isPrimaryMember) {
      const nonMemberContent = {
        'member-first': insuranceForm.getFieldValue('memberfirstName'),
        'member-last': insuranceForm.getFieldValue('memberlastName'),
        'member-birth': moment(
          `${insuranceForm.getFieldValue('memberbirthYear')}-${moment()
            .month(insuranceForm.getFieldValue('memberbirthMonth') - 1)
            .format('MM')}-${insuranceForm.getFieldValue('memberbirthDay')}`,
        ).format('YYYY-MM-DD'),
      };
      content = _.merge(content, nonMemberContent);
    }

    analytics.trackForm({
      pageType: analytics.pageTypes.FORM,
      pageName: `partner_form-booking-patient-information${
        insurance.provider ? '-insurance' : ''
      }`,
      name: 'form-booking',
      section: insurance.provider
        ? 'patient-information-insurance'
        : 'patient-details',
      content,
      practiceId: _.get(requestedAppointment, 'provider.clinic'),
      providerId: _.get(requestedAppointment, 'providerId'),
      userId: user.id,
      partnerId: user.partner.id,
      patientId: state.id,
    });
  };

  const checkRequiredFields = () => {
    const formValues = insuranceForm.getFieldsValue();

    // Disabling the continue button until all required inputs have been filled
    const requiredInputValues = [
      insuranceForm.getFieldValue('firstName'),
      insuranceForm.getFieldValue('lastName'),
      insuranceForm.getFieldValue('birthMonth'),
      insuranceForm.getFieldValue('birthDay'),
      insuranceForm.getFieldValue('birthYear'),
    ];

    if (insurance.provider) {
      if (country === COUNTRY.unitedStates) {
        requiredInputValues.push(
          insuranceForm.getFieldValue('insuranceCarrierUS'),
          insuranceForm.getFieldValue('insurancePlanUS'),
        );
      }

      if (country === COUNTRY.canada) {
        requiredInputValues.push(
          insuranceForm.getFieldValue('insuranceCarrierCanada'),
        );
      }

      if (!formValues.isPrimaryMember) {
        requiredInputValues.push(
          insuranceForm.getFieldValue('memberfirstName'),
          insuranceForm.getFieldValue('memberlastName'),
          insuranceForm.getFieldValue('memberbirthMonth'),
          insuranceForm.getFieldValue('memberbirthDay'),
          insuranceForm.getFieldValue('memberbirthYear'),
        );
      }
    }

    setIsContinueButtonDisabled(
      !requiredInputValues.every((input) => input !== '' && input !== undefined),
    );
  };

  const handleFormSubmit = (values) => {
    const patientDetails = {
      id: state.id,
      email: state.email,
      partnerId: user.partner.id,
      metadata: state.metadata,
    };

    let insuranceMemberMetadata = {};
    if (_.get(insurance, 'hasInsurance') === 'Yes') {
      insuranceMemberMetadata = {
        primaryInsuranceMember: values.isPrimaryMember,
        memberFirstName: values.memberfirstName,
        memberLastName: values.memberlastName,

        memberDateOfBirth: values.isPrimaryMember
          ? undefined
          : formatDateOfBirth(
            values.memberbirthYear,
            values.memberbirthMonth,
            values.memberbirthDay,
          ),

      };
    }

    patientDetails.metadata = patientDetails.metadata
      ? Object.assign(patientDetails.metadata, insuranceMemberMetadata)
      : {};
    patientDetails.firstName = values.firstName;
    patientDetails.lastName = values.lastName;
    patientDetails.dateOfBirth = formatDateOfBirth(
      values.birthYear,
      values.birthMonth,
      values.birthDay,
    );

    const newInsurance = makeInsuranceState(insuranceProvider, insurancePlan);
    const toSave = Object.assign({}, values, newInsurance);

    fireSubmitAnalytics();

    dispatch(updateCheckoutErrorStatus(null));
    dispatch(updateInsuranceProviderAndPlan(toSave));
    dispatch(updateCheckoutFormData(toSave));
    dispatch(updatePatientDetails(patientDetails));

    setFormData(toSave);
    setFormSubmitted(true);
  };

  const handleUpdateInsurance = (newProvider, newPlan) => {
    setInsuranceProvider(newProvider);
    if (country === COUNTRY.unitedStates) {
      setInsurancePlan(newPlan);
    }
  };

  if (isLoading) {
    return (
      <div data-testid="loading">
        <StepLoading type="loading" />
      </div>
    );
  }

  return (
    <>
      {checkoutFormErrorStatus
      && (
        <Error style={{ marginBottom: '30px' }}>
          <P color={colors.danger} data-testid="error">
            Something went wrong while submitting your details.
          </P>
        </Error>
      )}
      <Form
        form={insuranceForm}
        data-testid="insuranceForm"
        onFinish={handleFormSubmit}
        onChange={checkRequiredFields}
      >
        <Fieldset margin="0 0 20px">
          <section>
            <H1 fontSize="20px" margin="-12px 0 0 auto">
              Patient Information
              <StyledLock
                aria-hidden
                bottom="5px"
                margin="0 0 0 8px"
                size="16px"
                theme="filled"
                type="lock"
                role="presentation"
              />
            </H1>
            <P margin=" 0 0 10px">Who is coming in to see the dentist?</P>
            {insurance.provider
            && (
              <StyledAlert
                data-testid="patientInformationAlert"
                display="block"
                height="auto"
                fontSize="12px"
                margin="0 0 15px"
                message="Please ensure all fields are typed exactly as listed on the patient’s insurance plan so that practice can verify your coverage."
                textAlign="center"
                type="success"
              />
            )}
            <NameBirthContainer flex={insurance.provider}>
              <NameFormRow
                firstName={firstName}
                lastName={lastName}
                onChange={onNameChange}
              />
              <BirthDateFormRow
                day={birthdate.day}
                month={birthdate.month}
                year={birthdate.year}
                onChange={onBirthdateChange}
                requiredMessage=""
              />
            </NameBirthContainer>
          </section>
        </Fieldset>

        {insurance.provider
        && (
          <CheckoutInsuranceForm
            insuranceProvider={insuranceProvider}
            insuranceMemberId={insuranceMemberInformation.memberId}
            insuranceEmployer={insuranceMemberInformation.employer}
            groupNumber={insuranceMemberInformation.groupNumber}
            insurancePlan={insurancePlan}
            insuranceForm={insuranceForm}
            checkRequiredFields={checkRequiredFields}
            country={country}
            handleUpdateInsurance={handleUpdateInsurance}
            insuranceCardEnabled={false}
            checkMemberId={checkMemberId}
            contextType={ContextType.PARTNER}
            onMemberInformationChange={updateInsuranceMemberInformation}
            memberInformation={insuranceMemberInformation}
            isMemberIdOptional={memberIdIsOptional}
          />
        )}

        <StyledFormItem marginbottom="10px">
          <FlexRow justify="center">
            <MarketingButton
              type="primary"
              shape="round"
              size="large"
              block
              maxwidth="335px"
              data-testid="insuranceSubmitButton"
              disabled={isContinueButtonDisabled}
              htmlType="submit"
              loading={formSubmitted}
            >
              Continue
            </MarketingButton>
          </FlexRow>
        </StyledFormItem>

        <OpacityDiv className={isMemberIDHelperVisible ? 'hidden' : null}>
          <MemberIdModal
            onCancel={() => setIsMemberIDHelperVisible(false)}
            onOk={() => setIsMemberIDHelperVisible(false)}
            visible={isMemberIDHelperVisible}
          >
            <MemberIDHelperModal
              insuranceProvider={insuranceProvider.name}
              patient={patient}
              country={country}
            />
          </MemberIdModal>
        </OpacityDiv>
      </Form>
    </>
  );
};

InsuranceStep.propTypes = {
  handleNextStep: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
};

export default InsuranceStep;
